import { useMemo, useState, type ReactNode } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api.ts'
import { useModuleStatus } from '@/hooks/useModuleStatus.ts'
import {
  Phone, PhoneIncoming, PhoneOutgoing, PhoneMissed, PhoneCall,
  Clock, User, Building2, ArrowRight, CheckCircle, XCircle,
  RotateCcw, MessageSquare, Minus,
} from 'lucide-react'
import { useAnonymize } from '@/hooks/useAnonymize.ts'
import {
  Badge,
  BetaBadge,
  Button,
  Card,
  DataTable,
  Dropdown,
  DropdownContent,
  DropdownItem,
  DropdownTrigger,
  EmptyState,
  FilterBar,
  FilterChip,
  PageHeader,
  StatusBadge,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  useToast,
  type ColumnDef,
} from '@/components/ui'
import type { BadgeProps } from '@/components/ui'

interface Call {
  id: string
  type: 'incoming' | 'outgoing' | 'missed'
  callerNumber: string
  callerName?: string
  companyName?: string
  employeeName?: string
  duration?: number
  timestamp: string
  ticketId?: string
  contactReached?: boolean | null
}

interface Callback {
  id: string
  callerNumber: string
  callerName?: string
  companyName?: string
  subject?: string
  preferredTime?: string
  status: 'open' | 'in_progress' | 'done'
  assignedTo?: string
  createdAt: string
  contactReached?: boolean | null
}

interface CallStats {
  total: number
  incoming: number
  outgoing: number
  missed: number
}

type TabType = 'calls' | 'callbacks'
type CallFilter = 'all' | 'incoming' | 'outgoing' | 'missed'

const CALL_TYPE_META: Record<
  Call['type'],
  { icon: typeof PhoneIncoming; label: string; variant: BadgeProps['variant']; iconColor: string }
> = {
  incoming: { icon: PhoneIncoming, label: 'Eingehend', variant: 'success', iconColor: 'text-green-600' },
  outgoing: { icon: PhoneOutgoing, label: 'Ausgehend', variant: 'info', iconColor: 'text-blue-600' },
  missed: { icon: PhoneMissed, label: 'Verpasst', variant: 'danger', iconColor: 'text-red-600' },
}

function formatDuration(seconds?: number): string {
  if (!seconds) return '—'
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return `${m}:${String(s).padStart(2, '0')}`
}

function formatTime(dateStr: string): string {
  return new Date(dateStr).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })
}

export function TelefoniePage() {
  const a = useAnonymize()
  const { toast } = useToast()
  const { isBeta } = useModuleStatus('telefonie')
  const [activeTab, setActiveTab] = useState<TabType>('calls')
  const [callFilter, setCallFilter] = useState<CallFilter>('all')
  const queryClient = useQueryClient()

  const { data: stats } = useQuery<CallStats>({
    queryKey: ['call-stats'],
    queryFn: () => apiClient.get('/calls/stats').then((r) => r.data),
    retry: false,
  })

  const { data: calls = [], isLoading: callsLoading } = useQuery<Call[]>({
    queryKey: ['calls', callFilter],
    queryFn: () =>
      apiClient
        .get('/calls', { params: { type: callFilter !== 'all' ? callFilter : undefined } })
        .then((r) => r.data),
    retry: false,
  })

  const { data: callbacks = [], isLoading: callbacksLoading } = useQuery<Callback[]>({
    queryKey: ['callbacks'],
    queryFn: () => apiClient.get('/calls/callbacks').then((r) => r.data),
    retry: false,
    enabled: activeTab === 'callbacks',
  })

  const updateCallback = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      apiClient.patch(`/calls/callbacks/${id}`, { status }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['callbacks'] })
      toast({ title: 'Rückruf aktualisiert', tone: 'success' })
    },
    onError: () => toast({ title: 'Aktualisierung fehlgeschlagen', tone: 'danger' }),
  })

  const updateCallStatus = useMutation({
    mutationFn: ({ id, reached }: { id: string; reached: boolean | null }) =>
      apiClient.patch(`/calls/${id}/status`, { reached }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calls'] })
    },
    onError: () => toast({ title: 'Kontaktstatus konnte nicht gespeichert werden', tone: 'danger' }),
  })

  const updateCallbackContactStatus = useMutation({
    mutationFn: ({ id, reached }: { id: string; reached: boolean | null }) =>
      apiClient.patch(`/calls/callbacks/${id}`, { reached }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['callbacks'] })
    },
    onError: () => toast({ title: 'Kontaktstatus konnte nicht gespeichert werden', tone: 'danger' }),
  })

  const sendSmsReminder = useMutation({
    mutationFn: ({ phoneNumber, callbackId }: { phoneNumber: string; callbackId: string }) =>
      apiClient.post('/shipping/sms', {
        template: 'callback_reminder',
        phone: phoneNumber,
        callbackId,
      }),
    onSuccess: () => toast({ title: 'SMS-Erinnerung gesendet', tone: 'success' }),
    onError: () => toast({ title: 'SMS konnte nicht gesendet werden', tone: 'danger' }),
  })

  const openCallbacks = callbacks.filter((c) => c.status !== 'done')

  const statsCards = [
    { label: 'Gesamt heute', value: stats?.total ?? 0, icon: Phone, color: 'text-gray-700' },
    { label: 'Eingehend', value: stats?.incoming ?? 0, icon: PhoneIncoming, color: 'text-green-600' },
    { label: 'Ausgehend', value: stats?.outgoing ?? 0, icon: PhoneOutgoing, color: 'text-blue-600' },
    { label: 'Verpasst', value: stats?.missed ?? 0, icon: PhoneMissed, color: 'text-red-600' },
  ]

  const callColumns = useMemo<ColumnDef<Call, unknown>[]>(
    () => [
      {
        id: 'type',
        header: 'Typ',
        size: 140,
        cell: ({ row }) => {
          const meta = CALL_TYPE_META[row.original.type]
          const Icon = meta.icon
          return (
            <Badge variant={meta.variant} size="sm">
              <Icon className="w-3 h-3" />
              {meta.label}
            </Badge>
          )
        },
      },
      {
        id: 'caller',
        header: 'Anrufer',
        cell: ({ row }) => {
          const call = row.original
          return (
            <div className="min-w-0">
              <p className="text-body font-medium text-gray-900 truncate">
                {call.callerName ? a.name(call.callerName) : a.phone(call.callerNumber)}
              </p>
              <p className="text-caption text-gray-500">{a.phone(call.callerNumber)}</p>
            </div>
          )
        },
      },
      {
        id: 'company',
        header: 'Firma',
        cell: ({ row }) =>
          row.original.companyName ? (
            <span className="inline-flex items-center gap-1 text-small text-gray-600">
              <Building2 className="w-3 h-3" />
              {a.name(row.original.companyName)}
            </span>
          ) : (
            <span className="text-gray-400">—</span>
          ),
      },
      {
        accessorKey: 'timestamp',
        header: 'Zeit',
        size: 100,
        cell: ({ getValue }) => (
          <span className="text-small text-gray-700">{formatTime(getValue() as string)}</span>
        ),
      },
      {
        accessorKey: 'duration',
        header: 'Dauer',
        size: 100,
        cell: ({ getValue }) => (
          <span className="inline-flex items-center gap-1 text-caption text-gray-500 tabular-nums">
            <Clock className="w-3 h-3" />
            {formatDuration(getValue() as number | undefined)}
          </span>
        ),
      },
      {
        id: 'contactStatus',
        header: 'Kontaktstatus',
        size: 160,
        enableSorting: false,
        cell: ({ row }) => {
          const call = row.original
          const reached = call.contactReached
          let badge: ReactNode
          if (reached === true) {
            badge = (
              <Badge variant="success" size="sm" className="cursor-pointer">
                <CheckCircle className="w-3 h-3" />
                Erreicht
              </Badge>
            )
          } else if (reached === false) {
            badge = (
              <Badge variant="danger" size="sm" className="cursor-pointer">
                <XCircle className="w-3 h-3" />
                Nicht erreicht
              </Badge>
            )
          } else {
            badge = (
              <Badge variant="neutral" size="sm" className="cursor-pointer">
                <Minus className="w-3 h-3" />
                —
              </Badge>
            )
          }
          return (
            <Dropdown>
              <DropdownTrigger asChild>{badge}</DropdownTrigger>
              <DropdownContent align="start">
                <DropdownItem onClick={() => updateCallStatus.mutate({ id: call.id, reached: true })}>
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  Erreicht
                </DropdownItem>
                <DropdownItem onClick={() => updateCallStatus.mutate({ id: call.id, reached: false })}>
                  <XCircle className="w-4 h-4 text-red-600" />
                  Nicht erreicht
                </DropdownItem>
                <DropdownItem onClick={() => updateCallStatus.mutate({ id: call.id, reached: null })}>
                  <Minus className="w-4 h-4 text-gray-400" />
                  Nicht gesetzt
                </DropdownItem>
              </DropdownContent>
            </Dropdown>
          )
        },
      },
      {
        id: 'actions',
        header: '',
        enableSorting: false,
        size: 110,
        cell: ({ row }) =>
          row.original.type === 'missed' ? (
            <Button variant="secondary" size="xs" leadingIcon={<RotateCcw />}>
              Rückruf
            </Button>
          ) : null,
      },
    ],
    [a, updateCallStatus],
  )

  const callbackColumns = useMemo<ColumnDef<Callback, unknown>[]>(
    () => [
      {
        accessorKey: 'status',
        header: 'Status',
        size: 140,
        cell: ({ getValue }) => <StatusBadge status={getValue() as Callback['status']} />,
      },
      {
        id: 'caller',
        header: 'Anrufer',
        cell: ({ row }) => {
          const cb = row.original
          return (
            <div className="min-w-0">
              <p className="text-body font-medium text-gray-900 truncate">
                {cb.callerName ? a.name(cb.callerName) : a.phone(cb.callerNumber)}
              </p>
              <p className="text-caption text-gray-500">{a.phone(cb.callerNumber)}</p>
            </div>
          )
        },
      },
      {
        accessorKey: 'subject',
        header: 'Anliegen',
        cell: ({ getValue, row }) => {
          const subject = getValue() as string | undefined
          const preferred = row.original.preferredTime
          return (
            <div className="min-w-0">
              {subject ? (
                <p className="text-small text-gray-700 truncate">{a.freeText(subject)}</p>
              ) : (
                <p className="text-small text-gray-400">—</p>
              )}
              {preferred ? (
                <p className="text-caption text-gray-500 inline-flex items-center gap-1 mt-0.5">
                  <Clock className="w-3 h-3" />
                  Gewünscht: {preferred}
                </p>
              ) : null}
            </div>
          )
        },
      },
      {
        accessorKey: 'assignedTo',
        header: 'Zugewiesen',
        size: 160,
        cell: ({ getValue }) => {
          const v = getValue() as string | undefined
          return v ? (
            <span className="inline-flex items-center gap-1 text-small text-gray-600">
              <User className="w-3 h-3" />
              {v}
            </span>
          ) : (
            <span className="text-gray-400">—</span>
          )
        },
      },
      {
        id: 'contactStatus',
        header: 'Anruf-Status',
        size: 160,
        enableSorting: false,
        cell: ({ row }) => {
          const cb = row.original
          const reached = cb.contactReached
          let badge: ReactNode
          if (reached === true) {
            badge = (
              <Badge variant="success" size="sm" className="cursor-pointer">
                <CheckCircle className="w-3 h-3" />
                Erreicht
              </Badge>
            )
          } else if (reached === false) {
            badge = (
              <Badge variant="danger" size="sm" className="cursor-pointer">
                <XCircle className="w-3 h-3" />
                Nicht erreicht
              </Badge>
            )
          } else {
            badge = (
              <Badge variant="neutral" size="sm" className="cursor-pointer">
                <Minus className="w-3 h-3" />
                —
              </Badge>
            )
          }
          return (
            <Dropdown>
              <DropdownTrigger asChild>{badge}</DropdownTrigger>
              <DropdownContent align="start">
                <DropdownItem onClick={() => updateCallbackContactStatus.mutate({ id: cb.id, reached: true })}>
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  Erreicht
                </DropdownItem>
                <DropdownItem onClick={() => updateCallbackContactStatus.mutate({ id: cb.id, reached: false })}>
                  <XCircle className="w-4 h-4 text-red-600" />
                  Nicht erreicht
                </DropdownItem>
                <DropdownItem onClick={() => updateCallbackContactStatus.mutate({ id: cb.id, reached: null })}>
                  <Minus className="w-4 h-4 text-gray-400" />
                  Nicht gesetzt
                </DropdownItem>
              </DropdownContent>
            </Dropdown>
          )
        },
      },
      {
        id: 'actions',
        header: '',
        enableSorting: false,
        size: 360,
        cell: ({ row }) => {
          const cb = row.original
          return (
            <div className="flex items-center justify-end gap-2">
              {cb.status !== 'done' ? (
                <Button
                  variant="secondary"
                  size="xs"
                  leadingIcon={<MessageSquare />}
                  loading={sendSmsReminder.isPending}
                  onClick={() =>
                    sendSmsReminder.mutate({ phoneNumber: cb.callerNumber, callbackId: cb.id })
                  }
                >
                  SMS Erinnerung
                </Button>
              ) : null}
              {cb.status === 'open' ? (
                <Button
                  variant="secondary"
                  size="xs"
                  leadingIcon={<ArrowRight />}
                  onClick={() => updateCallback.mutate({ id: cb.id, status: 'in_progress' })}
                >
                  Übernehmen
                </Button>
              ) : null}
              {cb.status === 'in_progress' ? (
                <Button
                  variant="primary"
                  size="xs"
                  leadingIcon={<CheckCircle />}
                  onClick={() => updateCallback.mutate({ id: cb.id, status: 'done' })}
                >
                  Erledigt
                </Button>
              ) : null}
            </div>
          )
        },
      },
    ],
    [a, sendSmsReminder, updateCallback, updateCallbackContactStatus],
  )

  const callFilters: { value: CallFilter; label: string; icon?: typeof PhoneIncoming }[] = [
    { value: 'all', label: 'Alle' },
    { value: 'incoming', label: CALL_TYPE_META.incoming.label, icon: PhoneIncoming },
    { value: 'outgoing', label: CALL_TYPE_META.outgoing.label, icon: PhoneOutgoing },
    { value: 'missed', label: CALL_TYPE_META.missed.label, icon: PhoneMissed },
  ]

  return (
    <div className="space-y-4">
      <PageHeader
        title={
          <span className="flex items-center gap-3">
            <Phone className="w-6 h-6 text-brand-700" />
            Telefonie
            {isBeta ? <BetaBadge size="md" /> : null}
          </span>
        }
        description="Anrufe und Rückrufverwaltung"
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {statsCards.map((stat) => {
          const Icon = stat.icon
          return (
            <Card key={stat.label}>
              <Card.Body className="flex items-center gap-3">
                <Icon className={`w-8 h-8 ${stat.color}`} />
                <div>
                  <p className="text-h2 font-bold text-gray-900">{stat.value}</p>
                  <p className="text-caption text-gray-500">{stat.label}</p>
                </div>
              </Card.Body>
            </Card>
          )
        })}
      </div>

      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as TabType)}>
        <TabsList>
          <TabsTrigger value="calls">
            <PhoneCall className="w-4 h-4" />
            Anrufliste
          </TabsTrigger>
          <TabsTrigger value="callbacks">
            <RotateCcw className="w-4 h-4" />
            Rückrufe
            {openCallbacks.length > 0 ? (
              <Badge variant="danger" size="sm">
                {openCallbacks.length}
              </Badge>
            ) : null}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="calls" className="space-y-3">
          <FilterBar>
            {callFilters.map((f) => {
              const Icon = f.icon
              return (
                <FilterChip
                  key={f.value}
                  active={callFilter === f.value}
                  leadingIcon={Icon ? <Icon /> : undefined}
                  onClick={() => setCallFilter(f.value)}
                >
                  {f.label}
                </FilterChip>
              )
            })}
          </FilterBar>
          <DataTable<Call>
            data={calls}
            columns={callColumns}
            getRowId={(c) => c.id}
            loading={callsLoading}
            emptyState={
              <EmptyState
                title="Keine Anrufe gefunden"
                icon={<PhoneCall />}
                description="Für diesen Filter liegen keine Einträge vor."
              />
            }
          />
        </TabsContent>

        <TabsContent value="callbacks">
          <DataTable<Callback>
            data={callbacks}
            columns={callbackColumns}
            getRowId={(cb) => cb.id}
            loading={callbacksLoading}
            emptyState={
              <EmptyState
                title="Keine Rückrufe vorhanden"
                icon={<RotateCcw />}
                description="Sobald Rückrufwünsche eingehen, erscheinen sie hier."
              />
            }
          />
        </TabsContent>
      </Tabs>
    </div>
  )
}
